import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Divider } from '@percent/lemonade'
import {
  DisbursementStatus,
  DisbursementType
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { BulkActionMenuProps } from './BulkActionMenu.types'
import style from './BulkActionMenu.module.scss'

export function BulkActionMenu({
  selectedDisbursementIds,
  data,
  onOpenBulkRemovalModal,
  onOpenMarkAsApprovedBulkModal,
  onOpenMarkAsSentBulkModal,
  onSelectAllByStatus,
  tableContainerRef
}: Readonly<BulkActionMenuProps>) {
  const { t } = useTranslation()
  const [centerPosition, setCenterPosition] = useState<number | null>(null)

  useEffect(() => {
    const updatePosition = () => {
      if (tableContainerRef.current) {
        const rect = tableContainerRef.current.getBoundingClientRect()
        setCenterPosition(rect.left + rect.width / 2)
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    const resizeObserver = new ResizeObserver(updatePosition)

    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      resizeObserver.disconnect()
    }
  }, [tableContainerRef])

  const selectedStatus = data.find(disbursement => disbursement.id === selectedDisbursementIds[0])?.status

  const isPendingApproval = selectedStatus === DisbursementStatus.PENDING_APPROVAL
  const isApproved = selectedStatus === DisbursementStatus.PENDING_PAYMENT
  const isMultiApproval =
    selectedStatus === DisbursementStatus.PENDING_TRUSTEE_APPROVAL ||
    selectedStatus === DisbursementStatus.PENDING_AGENT_APPROVAL ||
    selectedStatus === DisbursementStatus.PENDING_MANAGER_APPROVAL
  const isFailed = selectedStatus === DisbursementStatus.FAILED

  const idsWithSelectedStatus = useMemo(() => {
    if (!selectedStatus) {
      return []
    }

    return data.filter(disbursement => disbursement.status === selectedStatus).map(disbursement => disbursement.id)
  }, [data, selectedStatus])

  const isAllSelectedByStatus = useMemo(() => {
    if (selectedDisbursementIds.length === 0) {
      return false
    }

    const selectedSet = new Set(selectedDisbursementIds)

    return idsWithSelectedStatus.length > 0 && idsWithSelectedStatus.every(id => selectedSet.has(id))
  }, [idsWithSelectedStatus, selectedDisbursementIds])

  const isApprovedChequeSelection = useMemo(() => {
    if (!isApproved || selectedDisbursementIds.length === 0) {
      return false
    }

    const selectedDisbursements = data.filter(disbursement => selectedDisbursementIds.includes(disbursement.id))

    return (
      selectedDisbursements.length > 0 &&
      selectedDisbursements.every(disbursement => disbursement.type === DisbursementType.CHEQUE)
    )
  }, [data, isApproved, selectedDisbursementIds])

  const menuStyle = centerPosition !== null ? { left: centerPosition } : undefined
  const selectedStatusLabel = useMemo(() => {
    if (isPendingApproval) {
      return t('status.pendingApproval').toLowerCase()
    }

    if (isApproved) {
      return t('status.pendingPayment').toLowerCase()
    }

    if (selectedStatus === DisbursementStatus.PENDING_TRUSTEE_APPROVAL) {
      return t('status.pendingFoundationApproval').toLowerCase()
    }

    if (selectedStatus === DisbursementStatus.PENDING_AGENT_APPROVAL) {
      return t('status.pendingAgentApproval').toLowerCase()
    }

    if (selectedStatus === DisbursementStatus.PENDING_MANAGER_APPROVAL) {
      return t('status.pendingManagerApproval').toLowerCase()
    }

    if (isFailed) {
      return t('status.failed').toLowerCase()
    }

    return null
  }, [isApproved, isPendingApproval, isFailed, selectedStatus, t])

  const shouldShowSelectAllByStatus = !!selectedStatus && !!selectedStatusLabel && idsWithSelectedStatus.length > 1

  if (selectedDisbursementIds.length === 0) {
    return null
  }

  return (
    <div className={style.bulkActions} style={menuStyle} data-testid="disbursements-bulk-actions">
      <div className={style.bulkActionsItem}>
        <span className={style.bulkActionsCount}>
          {t('typography.selected', { count: selectedDisbursementIds.length })}
        </span>

        {shouldShowSelectAllByStatus ? (
          <>
            <Divider orientation="vertical" />
            <button
              type="button"
              className={style.bulkActionsButton}
              onClick={() => onSelectAllByStatus(selectedStatus)}
              disabled={isAllSelectedByStatus}
              aria-label={t('typography.selectAllStatus', { status: selectedStatusLabel })}
              data-testid="disbursements-bulk-select-all-by-status"
            >
              {t('typography.selectAllStatus', { status: selectedStatusLabel })}
            </button>
          </>
        ) : null}
      </div>
      <Divider orientation="vertical" />
      {isPendingApproval && (
        <>
          <button
            type="button"
            className={style.bulkActionsButton}
            onClick={onOpenMarkAsApprovedBulkModal}
            aria-label={t('button.approve')}
            data-testid="disbursements-bulk-mark-as-approved"
          >
            {t('button.approve')}
          </button>
          <Divider orientation="vertical" />
        </>
      )}
      {isApprovedChequeSelection && (
        <>
          <button
            type="button"
            className={style.bulkActionsButton}
            onClick={onOpenMarkAsSentBulkModal}
            aria-label={t('button.markAsSent')}
            data-testid="disbursements-bulk-mark-as-sent"
          >
            {t('button.markAsSent')}
          </button>
          <Divider orientation="vertical" />
        </>
      )}
      {(isApproved || isPendingApproval || isMultiApproval || isFailed) && (
        <button
          type="button"
          className={style.bulkActionsButton}
          onClick={onOpenBulkRemovalModal}
          aria-label={t('button.remove')}
          data-testid="disbursements-bulk-remove"
        >
          {t('button.remove')}
        </button>
      )}
    </div>
  )
}
